import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';

import useStorage, { StorageProcessState } from '../../../hooks/useStorage';
import useComponentMounted from '../../../hooks/useComponentMounted';
import { storagePaths, pathPlaceholder } from '../../../utils/firebasePaths';
import LoadingOverlay from '../../components/LoadingOverlay';
import FormsModel, { FormState as State, FormType } from '../../Model/Form';
import Coach from '../../../Model/Coach';
import ConfirmationModal from '../../../components/v2/ConfirmationModal';
import FormsTable from '../../components/FormsTable';
import useToolsNavigation from '../../hooks/useToolsNavigation';
import useToast from '../../hooks/useToast';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import FirebaseContext from '../../../context/FirebaseContext';
import FormBuilder from '../../components/FormBuilder';
import CoachesListContext from '../../context/CoachesListContext';

import CoachSelectModal from './CoachSelectModal';
import TypeSelectModal from './TypeSelectModal';
import ActionContainer from './ActionContainer';
import {
  Container,
  HeaderContainer,
  Title,
  StyledCreationButton,
} from './styles';
import texts from './texts.json';

const Forms = () => {
  const [formState, setFormState] = useState(null);
  const [formsCollection, setformsCollection] = useState({ docs: [] });
  const [selectedForm, setSelectedForm] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showArchivedForms, setShowArchivedForms] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [formType, setFormType] = useState(null);
  const [showLeadForms, setShowLeadForms] = useState(true);
  const [showPostPaymentForms, setShowPostPaymentForms] = useState(true);
  // Confirmed default swaps, applied over the coach docs: the coaches collection is fetched
  // once on mount, so a swapped default would otherwise stay stale until a full reload.
  const [defaultOverrides, setDefaultOverrides] = useState({});
  // Form awaiting the "really make this the default?" confirmation, or null.
  const [pendingDefaultFormId, setPendingDefaultFormId] = useState(null);
  // defaultPostPaymentFormId per coach, read directly off each coach's doc (see below) —
  // covers inactive/S2 coaches too, unlike the active-external-coaches collection.
  const [coachDefaultFormIds, setCoachDefaultFormIds] = useState({});

  const isComponentMountedRef = useComponentMounted();
  const { navigateToForm } = useToolsNavigation();
  const { uploadJSON, getJSONData } = useStorage();
  const { showToast } = useToast();

  const {
    userId: createdBy,
  } = useContext(LoggedInUserContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { coachesCollection } = useContext(CoachesListContext);

  // A coach's default PPF is a pointer on their coach doc. Built from the coach ids present
  // in the loaded form rows — rather than the active-external-coaches collection, which
  // filters to isActive == true && isS2Coach == false — so an inactive or S2 coach's forms
  // still get an accurate Default chip / disabled Set Default state. Swaps confirmed by the
  // server in this session win over the fetched values.
  const defaultFormIdByCoachId = useMemo(() => (
    {
      ...coachDefaultFormIds,
      ...defaultOverrides,
    }
  ), [coachDefaultFormIds, defaultOverrides]);

  useEffect(() => {
    const loadCoachDefaultFormIds = async () => {
      const coachIds = [...new Set(formsCollection.docs.map((form) => form.coachId).filter(Boolean))];
      if (coachIds.length === 0) {
        if (isComponentMountedRef.current) {
          setCoachDefaultFormIds({});
        }
        return;
      }
      // Batched documentId-in queries (10 ids each) rather than one read per coach —
      // this page loads every form and nearly every coach owns one, so per-coach
      // reads were on the order of several hundred per page load.
      const coaches = await Coach.getCoachesByIds(coachIds);
      if (!isComponentMountedRef.current) return;
      const map = coaches.reduce((acc, coach) => {
        if (coach.defaultPostPaymentFormId) {
          acc[coach.id] = coach.defaultPostPaymentFormId;
        }
        return acc;
      }, {});
      setCoachDefaultFormIds(map);
    };
    loadCoachDefaultFormIds();
  }, [formsCollection, isComponentMountedRef]);

  const handleBackClick = () => {
    setFormState(null);
  };

  const handleTypeSelection = (type) => {
    setFormType(type);
    setShowTypeSelection(false);
    setFormState(State.NEW);
  };

  const handleFormDuplicateClick = (formId) => {
    const formDoc = formsCollection.docs.find((doc) => doc.id === formId);
    setSelectedForm(formDoc);
    setShowModal(true);
  };

  const handleFormArchiveClick = (formId) => {
    const formDoc = formsCollection.docs.find((doc) => doc.id === formId);
    setSelectedForm(formDoc);
    setShowConfirmation(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedForm({});
  };

  const duplicateForm = async (coach) => {
    const { data, id: existingFormId } = selectedForm;
    // We need to set the new coach name and id before saving the document
    const docData = {
      coach: coach.name,
      coachId: coach.id,
      formName: data.formName,
      type: data.type,
      createdBy,
      isArchived: false,
    };

    // Fetch form data from the storage JSON file
    const existingFormStoragePath = format(storagePaths.COACH_FORM_COLLECTION, {
      [pathPlaceholder.COACH_ID]: data.coachId,
    });
    const formData = await getJSONData(`${existingFormStoragePath}/${existingFormId}`);
    const {
      fields,
      hiddenFields,
      endScreenContent,
    } = formData;

    // Create the form config object with all the combined data
    const configData = {
      ...docData,
      fields,
      // Fields exclusive to lead forms
      ...(data.type === FormType.LEAD && {
        hiddenFields,
        endScreenContent,
      }),
    };

    // Reserve the doc id and upload the storage JSON to it BEFORE the Firestore doc exists,
    // then create the doc last. Mirrors FormSettings.jsx's create flow: a failed upload must
    // never leave behind a doc pointing at content that was never actually written.
    const newFormId = FormsModel.reserveFormId();
    const formConfigString = JSON.stringify(configData);
    const newStoragePath = format(storagePaths.COACH_FORM_COLLECTION, {
      [pathPlaceholder.COACH_ID]: coach.id,
    });
    const uploadResult = await uploadJSON(formConfigString, newStoragePath, newFormId);
    if (uploadResult?.state !== StorageProcessState.SUCCESS) {
      showToast(texts.duplicationUploadError, { error: true });
      return;
    }

    await FormsModel.addFormWithId(newFormId, docData);

    showToast(texts.duplicationSuccesful);
    handleModalClose();
  };

  const handleConfirmArchival = async () => {
    setIsLoading(true);
    setShowConfirmation(false);

    try {
      if (selectedForm.isArchived) {
        await selectedForm.unarchive();
      } else {
        if (selectedForm.type === FormType.POST_PAYMENT) {
          const activeForms = await FormsModel.getFormsByCoachId(selectedForm.coachId, FormType.POST_PAYMENT);

          // Don't allow to archive the only post payment form of a active coach
          const isCoachActive = coachesCollection.docs.find((coach) => coach.id === selectedForm.coachId);
          if (isCoachActive && activeForms.length === 1) {
            showToast(texts.validation.postPaymentArchival, { error: true });
            return;
          }

          // The default form can't be archived while other active PPFs exist —
          // a new default must be assigned first. `selectedForm.isDefault` is the
          // master-template flag, not "this coach's default" — archival for those rows
          // is already disabled, so the real case is the coach's defaultPostPaymentFormId
          // pointer, computed above as defaultFormIdByCoachId.
          //
          // That map is built from Coach.getAllExternalCoaches(), which filters to
          // isActive == true && isS2Coach == false — an inactive or S2 coach has no entry
          // there at all (as opposed to an entry that's simply not this form's id), which
          // would silently bypass the guard entirely for their forms. Fall back to a single
          // direct read for that one coach so the guard is authoritative regardless of
          // status; the map stays the fast path for the common case.
          const hasFastPathEntry = Object.prototype.hasOwnProperty.call(
            defaultFormIdByCoachId,
            selectedForm.coachId,
          );
          const defaultFormId = hasFastPathEntry
            ? defaultFormIdByCoachId[selectedForm.coachId]
            : (await Coach.getCoachById(selectedForm.coachId))?.defaultPostPaymentFormId || '';

          if (defaultFormId === selectedForm.id && activeForms.length > 1) {
            showToast(texts.validation.postPaymentDefaultArchival, { error: true });
            return;
          }
        }

        await selectedForm.archive();
      }
    } catch (error) {
      showToast(error.message, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    const collection = await FormsModel.getForms(showArchivedForms);

    if (isComponentMountedRef.current) {
      setformsCollection(collection);
      setIsLoading(false);
    }
  }, [isComponentMountedRef, showArchivedForms]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Stages the form for the "really make this the default?" confirmation below —
  // mirrors the archive flow's stage-then-confirm shape (handleFormArchiveClick /
  // handleConfirmArchival).
  const handleSetDefaultClick = (formId) => {
    setPendingDefaultFormId(formId);
  };

  const handleConfirmSetDefault = async () => {
    const formId = pendingDefaultFormId;
    setPendingDefaultFormId(null);
    setIsLoading(true);
    try {
      const response = await remote('forms-setDefaultPostPaymentForm', { formId });
      const { success, error } = await response.json();
      if (!success) {
        // A validation failure (e.g. a zod 400) can reply with a non-string error shape;
        // only surface it directly when it's actually a message meant for display.
        throw new Error(typeof error === 'string' ? error : texts.validation.setDefaultError);
      }
      const { coachId } = formsCollection.docs.find((doc) => doc.id === formId) || {};
      if (coachId) {
        setDefaultOverrides((prev) => ({ ...prev, [coachId]: formId }));
      }
      showToast(texts.setDefaultSuccessful);
      await fetchForms();
    } catch (error) {
      showToast(error.message, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const filteredForms = useMemo(() => (
    formsCollection.docs.filter((form) => {
      if (showLeadForms && showPostPaymentForms) {
        return true;
      }
      if (showLeadForms) {
        return form.type === FormType.LEAD;
      }
      if (showPostPaymentForms) {
        return form.type === FormType.POST_PAYMENT;
      }
      return false;
    })
  ), [formsCollection, showLeadForms, showPostPaymentForms]);

  const renderFormsTable = () => (
    <>
      <HeaderContainer>
        <Title>{texts.formsTitle}</Title>
        <StyledCreationButton onClick={() => setShowTypeSelection(true)}>
          {texts.newForm}
        </StyledCreationButton>
      </HeaderContainer>
      <FormsTable
        rows={filteredForms}
        ActionsCellComponent={ActionContainer}
        extraActionsCellProps={{
          navigateToForm,
          handleFormDuplicateClick,
          setIsLoading,
          handleFormArchiveClick,
          handleSetDefaultClick,
          defaultFormIdByCoachId,
        }}
        defaultFormIdByCoachId={defaultFormIdByCoachId}
        actionHeader={texts.actionHeader}
        handleArchivedToggle={setShowArchivedForms}
        showArchivedForms={showArchivedForms}
        handleLeadFormToggle={setShowLeadForms}
        showLeadForms={showLeadForms}
        handlePostPaymentFormToggle={setShowPostPaymentForms}
        showPostPaymentForms={showPostPaymentForms}
      />
      <CoachSelectModal
        showModal={showModal}
        onClose={handleModalClose}
        onConfirm={duplicateForm}
      />
    </>
  );

  return (
    <>
      {formState && formState === State.NEW
        ? <FormBuilder onBackClick={handleBackClick} type={formType} />
        : (
          <Container>
            {renderFormsTable()}
          </Container>
        )}
      <ConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => handleConfirmArchival()}
        title={
          selectedForm.isArchived
            ? format(texts.confirmationTitle.unarchive)
            : format(texts.confirmationTitle.archive)
        }
        description={
          selectedForm.isArchived
            ? format(texts.confirmationMessage.unarchive)
            : format(texts.confirmationMessage.archive)
        }
      />
      <ConfirmationModal
        open={!!pendingDefaultFormId}
        onClose={() => setPendingDefaultFormId(null)}
        onConfirm={handleConfirmSetDefault}
        title={texts.setDefaultDialog.title}
        description={format(texts.setDefaultDialog.content, {
          formName: formsCollection.docs.find((doc) => doc.id === pendingDefaultFormId)?.formName || '',
        })}
      />
      <LoadingOverlay isLoading={isLoading} />
      <TypeSelectModal
        showModal={showTypeSelection}
        onClose={() => setShowTypeSelection(false)}
        onConfirm={handleTypeSelection}
      />
    </>
  );
};

export default compose(
  observer,
)(Forms);
